import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { DEFAULT_CHAT_TITLE } from "@/lib/constants";
import { isFileDrag } from "@/lib/dnd/file-drag";
import { EnvironmentFilesPane } from "@/components/file-tree/EnvironmentFilesPane";
import { useDraftEnvironmentPreparation } from "@/components/file-tree/useDraftEnvironmentPreparation";
import { WorkspaceSearchPane } from "@/components/search/WorkspaceSearchPane";
import { ChatList, type ChatListItem } from "@/components/chat/ChatList";
import { ChatListFilterButton } from "@/components/chat/ChatListFilterButton";
import { applyChatListFilters } from "@/lib/chat-list-filters";
import { toMinSizePercent, usePanelGroupSize } from "@/lib/panel-min-size";
import { useLayoutStore } from "@/stores/useLayoutStore";
import {
	removeStoredEditorLayout,
	selectCurrentAgentId,
	selectCurrentAgentName,
	selectEffectiveEnvironmentId,
	useChatStore,
} from "@/stores/useChatStore";
import { useChatDraftStore } from "@/stores/useChatDraftStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { deleteChat, saveChat } from "@/lib/api";
import { setChatsArchived } from "@/lib/chat-archive";
import { useAgentSessionContext } from "@/components/chat/AgentSessionContext";
import { useChatActivitySync } from "@/components/chat/useChatActivitySync";
import { useToastStore } from "@/stores/useToastStore";
import { RenameChatDialog } from "@/components/chat/RenameChatDialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { SearchInput } from "@/components/common/SearchInput";
import { Icon } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { SectionHeader } from "@/components/common/SectionHeader";
import { FOCUS_CHAT_SEARCH_EVENT } from "@/lib/shortcut-defs";
import { useSearchStore } from "@/stores/useSearchStore";
import { confirmLeaveWithUnsavedFiles } from "@/stores/useUnsavedChangesGuardStore";
import { useOpenChat } from "@/components/chat/useOpenChat";
import { DEFAULT_LEFT_PANE_SIZES } from "@/stores/layout-state";

/** 左ペイン上端の余白。他のペイン (TOP_GAP) より広く取る。 */
const LEFT_PANE_TOP_GAP = 10;

/** 検索欄の左余白。虫眼鏡の左端を ChatList の項目文字 (ul 12px + button 10px) に揃える。 */
const CHAT_SEARCH_INDENT = 21;

/**
 * 折りたたみ中の段。見出しの高さだけを取り、残りは展開中の段が埋める。
 * PanelGroup が敷く flex 指定 (flexBasis: 0 / flexGrow: 比率) を上書きする。
 */
const COLLAPSED_PANEL_STYLE: CSSProperties = {
	flexBasis: "auto",
	flexGrow: 0,
	flexShrink: 0,
	overflow: "hidden",
};

/** 環境ファイル段の中身。展開中だけ段いっぱいに広げてスクロールさせる。 */
function environmentBoxStyle(collapsed: boolean): CSSProperties {
	return collapsed ? { overflow: "hidden" } : { height: "100%", overflowY: "auto" };
}

/**
 * 環境ファイルペインは環境未選択時・検索表示中はマウントされないため、その状態で OS から
 * ファイルをドロップするとブラウザがファイルを開いて画面遷移してしまう。子 (ペイン・行) が
 * 受け取ったドラッグは stopPropagation でここへ届かないので、届いたものだけを no-drop にする。
 */
function guardFileDrag(e: React.DragEvent) {
	if (!isFileDrag(e)) return;
	e.preventDefault();
	e.dataTransfer.dropEffect = "none";
}

export function LeftPane() {
	const location = useLocation();
	const leftPaneSizes = useLayoutStore((s) => s.leftPaneSizes);
	const setLeftPaneSizes = useLayoutStore((s) => s.setLeftPaneSizes);
	const chatId = useChatStore((s) => s.chatId);
	const effectiveEnvironmentId = useChatStore(selectEffectiveEnvironmentId);
	const currentAgentId = useChatStore(selectCurrentAgentId);
	const currentAgentName = useChatStore(selectCurrentAgentName);
	const allChats = useChatStore((s) => s.chatList);
	// チャット画面は今のエージェント (開いているチャット、新規なら対象のエージェント) のチャットだけを出す。
	const chatList = useMemo(
		() =>
			currentAgentId === null
				? allChats
				: allChats.filter((c) => c.environmentId === currentAgentId),
		[allChats, currentAgentId],
	);
	const chatIndicators = useChatStore((s) => s.chatIndicators);
	const chatLastActivityAt = useChatStore((s) => s.chatLastActivityAt);
	const chatListFilters = useChatStore((s) => s.chatListFilters);
	const setChatListFilters = useChatStore((s) => s.setChatListFilters);
	const currentUserId = useCurrentUserStore((s) => s.id);
	const { startNewChatDraft } = useAgentSessionContext();
	const { preparationFailed, retryPreparation } = useDraftEnvironmentPreparation(
		location.pathname === "/chats/new",
	);
	const searchOpen = useSearchStore((s) => s.searchOpen);
	const openSearch = useSearchStore((s) => s.openSearch);
	const environmentFilesCollapsed = useLayoutStore((s) => s.environmentFilesCollapsed);
	const toggleEnvironmentFiles = useLayoutStore((s) => s.toggleEnvironmentFiles);
	const chatListCollapsed = useLayoutStore((s) => s.chatListCollapsed);
	const setChatListCollapsed = useLayoutStore((s) => s.setChatListCollapsed);
	const toggleChatList = useLayoutStore((s) => s.toggleChatList);
	// 検索ペインは上段を占有するため、開いている間は環境ファイルの折りたたみを無視する。
	const filesCollapsed = environmentFilesCollapsed && !searchOpen;

	const navigate = useNavigate();

	useChatActivitySync();

	const [query, setQuery] = useState("");
	const [renameTarget, setRenameTarget] = useState<{
		chatId: string;
		currentTitle: string;
	} | null>(null);
	const [deleteTargets, setDeleteTargets] = useState<ChatListItem[]>([]);
	const searchRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const focus = () => {
			setChatListCollapsed(false);
			// 折りたたみ中は入力が未マウントのため、展開後の描画を待ってからフォーカスする。
			requestAnimationFrame(() => {
				searchRef.current?.focus();
				searchRef.current?.select();
			});
		};
		window.addEventListener(FOCUS_CHAT_SEARCH_EVENT, focus);
		return () => window.removeEventListener(FOCUS_CHAT_SEARCH_EVENT, focus);
	}, [setChatListCollapsed]);

	const visibleChatList = useMemo(
		() => applyChatListFilters(chatList, chatListFilters, currentUserId),
		[chatList, chatListFilters, currentUserId],
	);

	const chatItems = useMemo<ChatListItem[]>(
		() =>
			visibleChatList.map((c) => ({
				chatId: c.id,
				label: c.title ?? DEFAULT_CHAT_TITLE,
				archived: c.archived,
				environmentDeleted: c.environmentId === null,
				indicator: chatIndicators[c.id] ?? null,
				updatedAt: chatLastActivityAt[c.id] ?? c.updatedAt,
			})),
		[visibleChatList, chatIndicators, chatLastActivityAt],
	);

	const filteredChatItems = useMemo(() => {
		if (!query.trim()) return chatItems;
		const q = query.toLowerCase();
		return chatItems.filter((s) => s.label.toLowerCase().includes(q));
	}, [chatItems, query]);

	// 未保存のファイルを確かめてから URL を変える。読み込みと切り替え (SSE の切断を含む) は ChatUrlSync が行う (issue #60)。
	const openChat = useOpenChat();

	// バックエンドにチャットを作らず、空の作成ビューを表示するだけにする。
	// 実際のチャット作成は最初のメッセージ送信時に行う (CenterPane.handleSend)。
	// 下書きでも同じエージェントのまま続けられるよう、今のエージェントを引き継ぐ。
	// 送信先は NewChatEnvironmentStatus が稼働中と確かめてから決める。
	const startDraftForCurrentAgent = useCallback(() => {
		const store = useChatStore.getState();
		const agentId = selectCurrentAgentId(store);
		const agentName = selectCurrentAgentName(store);
		startNewChatDraft();
		if (agentId !== null) useChatStore.getState().setNewChatAgent({ id: agentId, name: agentName });
		navigate("/chats/new");
	}, [navigate, startNewChatDraft]);

	const handleNewChatClick = useCallback(async () => {
		if (await confirmLeaveWithUnsavedFiles()) startDraftForCurrentAgent();
	}, [startDraftForCurrentAgent]);

	const handleRenameRequest = useCallback((targetChatId: string, currentTitle: string) => {
		setRenameTarget({ chatId: targetChatId, currentTitle });
	}, []);

	const handleArchive = useCallback(
		(targetChatIds: string[]) => setChatsArchived(targetChatIds, true),
		[],
	);

	const handleUnarchive = useCallback(
		(targetChatIds: string[]) => setChatsArchived(targetChatIds, false),
		[],
	);

	// 削除はサーバー側で確定する (メッセージ・ジョブ・モニター・学習記録も消える)。失敗は
	// ConfirmDialog がダイアログ内に表示し、成功済みの分は対象から外して再試行できるようにする。
	// 成功したらローカルの一覧・下書き・レイアウト残骸も消し、開いていたチャットなら新規チャットビューへ退避する。
	const handleDeleteConfirm = useCallback(async () => {
		const openChatId = useChatStore.getState().chatId;
		const deletesOpenChat = deleteTargets.some((t) => t.chatId === openChatId);
		if (deletesOpenChat && !(await confirmLeaveWithUnsavedFiles())) {
			throw new Error(
				"開いているチャットに未保存のファイルがあります。保存してから削除してください。",
			);
		}
		for (const target of deleteTargets) {
			await deleteChat(target.chatId);
			setDeleteTargets((current) => current.filter((t) => t.chatId !== target.chatId));
			const store = useChatStore.getState();
			store.removeChatListItem(target.chatId);
			useChatDraftStore.getState().clearDraft(target.chatId);
			removeStoredEditorLayout(target.chatId);
			if (store.chatId === target.chatId) startDraftForCurrentAgent();
		}
		useToastStore.getState().show({
			type: "success",
			message:
				deleteTargets.length === 1
					? "チャットを削除しました。"
					: `${deleteTargets.length} 件のチャットを削除しました。`,
		});
	}, [deleteTargets, startDraftForCurrentAgent]);

	const handleRenameConfirm = useCallback(
		async (newName: string) => {
			if (!renameTarget) return;
			await saveChat({
				chatId: renameTarget.chatId,
				title: newName,
			});
			const store = useChatStore.getState();
			store.updateChatListItem(renameTarget.chatId, { title: newName });
			if (store.chatId === renameTarget.chatId) store.setCurrentTitle(newName);
			setRenameTarget(null);
		},
		[renameTarget],
	);

	const environmentSection = (
		<>
			{searchOpen ? (
				<WorkspaceSearchPane />
			) : (
				effectiveEnvironmentId && (
					<EnvironmentFilesPane
						onOpenSearch={openSearch}
						preparationFailed={preparationFailed}
						retryPreparation={retryPreparation}
						collapsed={filesCollapsed}
						onToggleCollapsed={toggleEnvironmentFiles}
					/>
				)
			)}
			{!effectiveEnvironmentId && (
				<p
					style={{
						padding: "12px",
						fontSize: "var(--text-caption)",
						color: "var(--fg-3)",
					}}
				>
					ファイルがありません
				</p>
			)}
		</>
	);

	const chatSection = (
		<div
			style={{
				height: "100%",
				minHeight: 0,
				display: "grid",
				gridTemplateRows: chatListCollapsed ? "auto" : "auto auto auto minmax(0, 1fr)",
				gridTemplateColumns: "minmax(0, 1fr)",
			}}
		>
			<SectionHeader
				label="チャット"
				collapsed={chatListCollapsed}
				onToggleCollapsed={toggleChatList}
				headingTestId="chats-heading"
				toggleTestId="chat-list-toggle"
			>
				{!chatListCollapsed && (
					<IconButton
						icon="chatPlus"
						color="var(--fg-3)"
						onClick={handleNewChatClick}
						title="新規チャット"
						data-testid="new-chat-btn"
					/>
				)}
			</SectionHeader>
			{!chatListCollapsed && (
				<>
					{/* チャット検索。右端に絞り込みボタンを置く。 */}
					<div style={{ padding: `0 12px 4px ${CHAT_SEARCH_INDENT}px` }}>
						<SearchInput
							ref={searchRef}
							value={query}
							onChange={setQuery}
							placeholder="チャットを検索"
							testId="session-search-input"
							trailing={
								<ChatListFilterButton filters={chatListFilters} onChange={setChatListFilters} />
							}
						/>
					</div>

					{/* チャット一覧 */}
					<div style={{ minHeight: 0, overflowY: "auto", paddingBottom: 8 }}>
						<ChatList
							items={filteredChatItems}
							activeChatId={chatId}
							onSelect={openChat}
							onRename={handleRenameRequest}
							onArchive={handleArchive}
							onUnarchive={handleUnarchive}
							onDelete={setDeleteTargets}
						/>
					</div>
				</>
			)}
		</div>
	);

	return (
		<div
			data-testid="left-pane"
			style={{
				height: "100%",
				minHeight: 0,
				minWidth: 0,
				overflow: "hidden",
				display: "grid",
				gridTemplateRows: "auto minmax(0, 1fr)",
				gridTemplateColumns: "minmax(0, 1fr)",
				paddingTop: LEFT_PANE_TOP_GAP,
				background: "var(--surface-1)",
			}}
		>
			<AgentNameHeader name={currentAgentName} />
			<LeftPaneSections
				sizes={leftPaneSizes}
				filesCollapsed={filesCollapsed}
				chatListCollapsed={chatListCollapsed}
				onLayout={setLeftPaneSizes}
				environmentSection={environmentSection}
				chatSection={chatSection}
			/>

			<RenameChatDialog
				open={renameTarget !== null}
				initialName={renameTarget?.currentTitle ?? ""}
				onConfirm={handleRenameConfirm}
				onCancel={() => setRenameTarget(null)}
			/>
			<ConfirmDialog
				open={deleteTargets.length > 0}
				title="チャットを削除"
				message={
					deleteTargets.length > 1
						? `${deleteTargets.length} 件のチャットを削除しますか？メッセージ履歴を含むこれらのチャットのデータはすべて削除され、元に戻せません。`
						: `チャット「${deleteTargets[0]?.label ?? ""}」を削除しますか？メッセージ履歴を含むこのチャットのデータはすべて削除され、元に戻せません。`
				}
				confirmLabel="削除"
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeleteTargets([])}
			/>
		</div>
	);
}

/**
 * 左ペインの先頭に出す今のエージェントの名前。ファイルとチャットの両方がこのエージェントのものだと示す。
 * 名前が届いたときに下の段がずれないよう、名前が無い間も 1 行分の高さを取っておく。
 */
function AgentNameHeader({ name }: { name: string | null }) {
	return (
		<div
			data-testid="left-pane-agent-name"
			style={{
				display: "flex",
				alignItems: "center",
				gap: 6,
				minWidth: 0,
				padding: "0 12px 8px",
				borderBottom: "var(--pane-header-border)",
				color: "var(--fg-3)",
			}}
		>
			{name !== null && <Icon name="agent" size={14} />}
			<span
				title={name ?? undefined}
				data-tooltip-if-truncated
				style={{
					minWidth: 0,
					minHeight: "1lh",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					fontSize: "var(--text-ui)",
					fontWeight: 600,
					color: "var(--fg-1)",
				}}
			>
				{name}
			</span>
		</div>
	);
}

/** 段 1 つの最小高 (px)。見出しと数行が残る高さ。 */
const SECTION_MIN_HEIGHT = 96;

/** 左ペインの高さを測り終えるまで使う下限 (%)。初回描画の 1 フレームだけ効く。 */
const SECTION_MIN_SIZE_FALLBACK = 15;

interface LeftPaneSectionsProps {
	sizes: number[];
	filesCollapsed: boolean;
	chatListCollapsed: boolean;
	onLayout: (sizes: number[]) => void;
	/** 各段の中身。実測のたびに作り直さないよう、要素のまま受け取る。 */
	environmentSection: ReactNode;
	chatSection: ReactNode;
}

/**
 * 環境ファイル / チャットの上下分割。折りたたみ中の段は高さを分け合わない。
 * 折りたたみで PanelGroup ごと差し替えると中身が再マウントされ、ツリーの展開状態や
 * 選択が消えるため、構造は保ったまま段のスタイルとハンドルの可否だけを切り替える。
 * 段の下限は左ペインの実寸から px で決める。% のままだと縦に長いディスプレイでは
 * 下限が高くなりすぎ、短いディスプレイでは見出しだけの高さまで潰せてしまう。
 */
function LeftPaneSections({
	sizes,
	filesCollapsed,
	chatListCollapsed,
	onLayout,
	environmentSection,
	chatSection,
}: LeftPaneSectionsProps) {
	const { ref, size: groupHeight } = usePanelGroupSize("vertical");
	const minSize = toMinSizePercent(SECTION_MIN_HEIGHT, groupHeight, SECTION_MIN_SIZE_FALLBACK);

	return (
		<div ref={ref} style={{ height: "100%", minHeight: 0 }}>
			<PanelGroup direction="vertical" onLayout={onLayout}>
				<Panel
					defaultSize={sizes[0] ?? DEFAULT_LEFT_PANE_SIZES[0]}
					minSize={minSize}
					style={filesCollapsed ? COLLAPSED_PANEL_STYLE : undefined}
					className="izanagi-environment-drop-frame"
				>
					<div
						style={environmentBoxStyle(filesCollapsed)}
						onDragOver={guardFileDrag}
						onDrop={guardFileDrag}
					>
						{environmentSection}
					</div>
				</Panel>
				{/* 折りたたみ中は動かせないが、境界線は同じ見た目で残す。 */}
				<PanelResizeHandle
					className="izanagi-resize-handle-row"
					disabled={filesCollapsed || chatListCollapsed}
				/>
				<Panel
					defaultSize={sizes[1] ?? DEFAULT_LEFT_PANE_SIZES[1]}
					minSize={minSize}
					style={chatListCollapsed ? COLLAPSED_PANEL_STYLE : undefined}
				>
					{chatSection}
				</Panel>
			</PanelGroup>
		</div>
	);
}
